import React from 'react';
import { getLogoSrc } from '../utils/logoHelper';
import { formatGameDate, formatGameTime } from '../utils/dateFormatter';
import './GraphicTryout.css';
import api from '../services/api';

const API_BASE_URL = api.defaults.baseURL.replace('/api', '');

function GraphicTryout({
  graphicData,
  teamSport,
  teamColors,
  backgroundStyle = 'linear-gradient',
  homeTeamLogo,
  showWatermark = true,
  dateFormat = 'full',
  heroFont = 'Bebas Neue',
  heroFontSize = 160,
  accentBlockColor = null,
  detailTextColor = null,
}) {
  const {
    homeTeam,
    ageGroup,
    tryoutDate,
    tryoutTime,
    location,
    contact,
    additionalInfo,
  } = graphicData;

  const accent = accentBlockColor || teamColors.secondary;
  const detailColor = detailTextColor || null;

  const getBackgroundStyle = () => {
    switch (backgroundStyle) {
      case 'linear-gradient':
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'radial-gradient':
        return `radial-gradient(circle at center, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'diagonal-gradient':
        return `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
      case 'split-two-tone':
        return `linear-gradient(90deg, ${teamColors.primary} 0%, ${teamColors.primary} 50%, ${teamColors.secondary} 50%, ${teamColors.secondary} 100%)`;
      case 'solid-primary':
        return teamColors.primary;
      case 'solid-secondary':
        return teamColors.secondary;
      default:
        return `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`;
    }
  };

  const cardStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    background: '#ffffff',
    padding: '28px 32px',
    borderRadius: 24,
  };

  const labelStyle = {
    fontSize: 24,
    fontWeight: 700,
    color: detailColor || '#718096',
    opacity: 0.7,
    textTransform: 'uppercase',
    letterSpacing: 1,
    flexShrink: 0,
  };

  const valueStyle = {
    fontSize: 28,
    fontWeight: 600,
    color: detailColor || '#2d3748',
    lineHeight: 1.3,
    wordWrap: 'break-word',
  };

  return (
    <div
      className="graphic-tryout"
      id="graphic-tryout"
      style={{
        background: getBackgroundStyle(),
        display: 'flex',
        flexDirection: 'column',
        padding: 56,
        borderRadius: 40,
        gap: 20,
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontFamily: heroFont,
            fontSize: heroFontSize * 0.21 + 'px',
            fontWeight: 900,
            color: 'white',
            textTransform: 'uppercase',
            textShadow: '6px 6px 16px rgba(0,0,0,0.5)',
            letterSpacing: '2px',
            lineHeight: 1.15,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            WebkitLineClamp: 2,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {homeTeam || 'TEAM NAME'} TRYOUTS
        </div>
      </div>

      {/* Logo + Looking For */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 32,
          padding: 40,
          borderRadius: 32,
          border: '4px solid rgba(255,255,255,0.4)',
        }}
      >
        <div
          style={{
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: '#ffffff',
            border: '4px solid #ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {homeTeamLogo ? (
            <img
              src={getLogoSrc(homeTeamLogo, API_BASE_URL)}
              alt={homeTeam}
              crossOrigin="anonymous"
              style={{ height: 96, width: 'auto', maxWidth: 96 }}
            />
          ) : (
            <span style={{ fontSize: '72px' }}>
              {teamSport === 'Softball' ? '🥎' : '⚾'}
            </span>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: 'rgba(255,255,255,0.9)',
              letterSpacing: 2,
              textTransform: 'uppercase',
            }}
          >
            LOOKING FOR:
          </div>
          <div
            style={{
              fontSize: 56,
              fontWeight: 900,
              color: 'white',
              textTransform: 'uppercase',
              textShadow: '4px 4px 12px rgba(0,0,0,0.4)',
              lineHeight: 1.2,
            }}
          >
            {ageGroup || 'ALL AGES'}
          </div>
        </div>
      </div>

      {/* Date & Time */}
      {(tryoutDate || tryoutTime) && (
        <div style={{ ...cardStyle, justifyContent: 'flex-start', gap: 64, borderColor: accent }}>
          {tryoutDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={labelStyle}>DATE</span>
              <span style={valueStyle}>{formatGameDate(tryoutDate, dateFormat)}</span>
            </div>
          )}
          {tryoutTime && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span style={labelStyle}>TIME</span>
              <span style={valueStyle}>{formatGameTime(tryoutTime)}</span>
            </div>
          )}
        </div>
      )}

      {/* Location */}
      {location && (
        <div style={{ ...cardStyle, borderColor: accent }}>
          <span style={labelStyle}>LOCATION</span>
          <span style={valueStyle}>{location}</span>
        </div>
      )}

      {/* Additional Info */}
      {additionalInfo && (
        <div
          style={{
            background: '#ffffff',
            padding: '28px 32px',
            borderRadius: 24,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              fontSize: 28,
              color: '#2d3748',
              lineHeight: 1.4,
              fontWeight: 500,
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
            }}
          >
            {additionalInfo}
          </div>
        </div>
      )}

      {/* Contact */}
      {contact && (
        <div style={{ ...cardStyle, marginTop: 'auto', borderColor: accent }}>
          <span style={labelStyle}>CONTACT</span>
          <span style={valueStyle}>{contact}</span>
        </div>
      )}

      {showWatermark && (
        <div className="playmaker-watermark">
          <img src="/PMlogoleft.png" alt="Playmaker Media Kit" />
        </div>
      )}
    </div>
  );
}

export default GraphicTryout;
